import { useState, useCallback, useEffect, useRef } from 'react'
import { useReverseGeocode, AddressSuggestion } from '../../api/addresses'
import { MapView } from './MapView'
import { AddressSearch } from './AddressSearch'
import styles from './LocationPicker.module.css'

export interface Location {
  lat: number
  lng: number
  address: string
  buildingNumber?: string
  floor?: string
  notes?: string
}

interface LocationPickerProps {
  value?: Location
  onChange: (location: Location) => void
  showDetailFields?: boolean
}

export function LocationPicker({ value, onChange, showDetailFields = true }: LocationPickerProps) {
  const [address, setAddress] = useState(value?.address ?? '')
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(
    value ? { lat: value.lat, lng: value.lng } : null
  )
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    value ? [value.lng, value.lat] : [106.8456, -6.2088]
  )
  const [geoError, setGeoError] = useState<string | null>(null)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showPrePermissionModal, setShowPrePermissionModal] = useState(false)
  const [isLocating, setIsLocating] = useState(false)
  const [hasAskedPermission, setHasAskedPermission] = useState(false)
  const [buildingNumber, setBuildingNumber] = useState(value?.buildingNumber ?? '')
  const [floor, setFloor] = useState(value?.floor ?? '')
  const [notes, setNotes] = useState(value?.notes ?? '')
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)
  const retryCountRef = useRef(0)

  const updateLocation = useCallback(() => {
    if (selectedCoords && address) {
      onChange({
        lat: selectedCoords.lat,
        lng: selectedCoords.lng,
        address,
        buildingNumber: buildingNumber || undefined,
        floor: floor || undefined,
        notes: notes || undefined,
      })
    }
  }, [selectedCoords, address, buildingNumber, floor, notes, onChange])

  useEffect(() => {
    updateLocation()
  }, [buildingNumber, floor, notes, updateLocation])

  const handleMapCenterChange = useCallback((lngLat: [number, number]) => {
    const [lng, lat] = lngLat
    setSelectedCoords({ lat, lng })
    setAddress('') // Clear to trigger reverse geocode
    setIsLoadingAddress(true)
    // Immediately notify parent with new coords (address will be updated after reverse geocode)
    onChange({
      lat,
      lng,
      address: '', // Will be filled by reverse geocode
      buildingNumber: buildingNumber || undefined,
      floor: floor || undefined,
      notes: notes || undefined,
    })
  }, [onChange, buildingNumber, floor, notes])

  const handleAddressSelect = useCallback((suggestion: AddressSuggestion) => {
    const { lat, lng } = suggestion.coordinates
    setSelectedCoords({ lat, lng })
    setMapCenter([lng, lat])
    setAddress(suggestion.displayName)
    onChange({
      lat,
      lng,
      address: suggestion.displayName,
      buildingNumber: buildingNumber || undefined,
      floor: floor || undefined,
      notes: notes || undefined,
    })
  }, [onChange, buildingNumber, floor, notes])

  const handleReverseGeocodeResult = useCallback((suggestion: AddressSuggestion) => {
    if (!selectedCoords || address) return

    const nextAddress = suggestion.displayName
    setAddress(nextAddress)
    setIsLoadingAddress(false)
    onChange({
      lat: selectedCoords.lat,
      lng: selectedCoords.lng,
      address: nextAddress,
      buildingNumber,
      floor,
      notes,
    })
  }, [selectedCoords, address, buildingNumber, floor, notes, onChange])

  useReverseGeocode(
    selectedCoords?.lat ?? 0,
    selectedCoords?.lng ?? 0,
    !!selectedCoords && !address,
    handleReverseGeocodeResult
  )

  const requestLocation = useCallback(function requestLocationInner(
    highAccuracy: boolean,
    isRetry: boolean = false
  ) {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords
        setSelectedCoords({ lat, lng })
        setMapCenter([lng, lat])
        setAddress('')
        setGeoError(null)
        setIsLocating(false)
        setShowLocationModal(false)
        retryCountRef.current = 0
      },
      (err) => {
        console.error('Geolocation error:', err)
        
        // POSITION_UNAVAILABLE (code 2) - try with low accuracy first, then retry
        if (err.code === 2 && highAccuracy && !isRetry) {
          // First attempt with high accuracy failed, try low accuracy
          requestLocationInner(false, false)
          return
        }
        
        if (err.code === 2 && retryCountRef.current < 2) {
          // Retry up to 2 times with a delay
          retryCountRef.current++
          console.log(`Retry attempt ${retryCountRef.current}...`)
          setTimeout(() => requestLocationInner(false, true), 1500)
          return
        }
        
        setIsLocating(false)
        retryCountRef.current = 0
        
        if (err.code === 1) {
          // Permission denied - show modal with instructions
          setShowLocationModal(true)
          setGeoError(null)
        } else if (err.code === 2) {
          // Position unavailable after retries
          setGeoError('Unable to determine location. Try moving to an open area or use address search.')
        } else {
          setGeoError('Location request timed out. Please try again.')
        }
      },
      { 
        timeout: highAccuracy ? 10000 : 15000, 
        enableHighAccuracy: highAccuracy,
        maximumAge: 30000 // Accept cached position up to 30 seconds old
      }
    )
  }, [])

  const triggerLocationRequest = useCallback(async () => {
    setGeoError(null)
    setHasAskedPermission(true)
    setIsLocating(true)
    retryCountRef.current = 0
    
    // Start with high accuracy, will fallback to low accuracy if needed
    requestLocation(true, false)
  }, [requestLocation])

  const handleUseMyLocation = useCallback(async () => {
    setGeoError(null)
    
    if (!navigator.geolocation) {
      setGeoError('Geolocation not supported by your browser')
      return
    }

    // Check if we're on HTTPS (required for geolocation on iOS)
    if (typeof window !== 'undefined' && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setGeoError('Location requires HTTPS connection')
      return
    }
    
    // Check permissions API if available (not on Safari/iOS)
    if (navigator.permissions) {
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' })
        if (status.state === 'denied') {
          setShowLocationModal(true)
          return
        }
        if (status.state === 'granted') {
          // Already granted, proceed directly
          triggerLocationRequest()
          return
        }
        // status.state === 'prompt' - show pre-permission modal
      } catch {
        // Permissions API not supported (Safari), continue with check
      }
    }
    
    // If we haven't asked before, show pre-permission modal to explain
    if (!hasAskedPermission) {
      setShowPrePermissionModal(true)
      return
    }
    
    // Already asked before, proceed directly
    triggerLocationRequest()
  }, [hasAskedPermission, triggerLocationRequest])

  const handleCloseModal = useCallback(() => {
    setShowLocationModal(false)
  }, [])

  const isLocationSelected = selectedCoords && address

  return (
    <div className={styles.picker}>
      {/* Section 01: Location Search */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionNumber}>01</span>
          <span className={styles.sectionTitle}>FIND LOCATION</span>
        </div>
        
        <div className={styles.searchRow}>
          <div className={styles.searchWrapper}>
            <AddressSearch
              value={address}
              onChange={setAddress}
              onSelect={handleAddressSelect}
              placeholder="Search address or click on map..."
            />
          </div>
          <button 
            type="button"
            onClick={handleUseMyLocation}
            className={`${styles.locationButton} ${isLocating ? styles.locationButtonLoading : ''}`}
            aria-label="Use my location"
            disabled={isLocating}
          >
            {isLocating ? (
              <>
                <div className={styles.buttonSpinner} />
                <span className={styles.locationButtonText} aria-hidden="true">...</span>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v4m0 12v4M2 12h4m12 0h4"/>
                </svg>
                <span className={styles.locationButtonText} aria-hidden="true">GPS</span>
              </>
            )}
          </button>
        </div>
        
        {geoError && (
          <div className={styles.errorBanner}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4m0 4h.01"/>
            </svg>
            {geoError}
          </div>
        )}
      </div>

      {/* Map Container */}
      <div className={styles.mapSection}>
        <div className={styles.mapContainer}>
          <MapView
            center={mapCenter}
            zoom={14}
            onCenterChange={handleMapCenterChange}
            showCenterPin
            className={styles.map}
          />
          <div className={styles.mapOverlay}>
            <span className={styles.mapHint}>DRAG MAP TO POSITION PIN</span>
          </div>
        </div>
        
        {isLoadingAddress && selectedCoords && (
          <div className={styles.loadingBanner}>
            <div className={styles.loadingSpinner} />
            <span>FINDING ADDRESS...</span>
          </div>
        )}
        
        {isLocationSelected && !isLoadingAddress && (
          <div className={styles.selectedBanner}>
            <div className={styles.selectedIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div className={styles.selectedContent}>
              <span className={styles.selectedLabel}>LOCATION CONFIRMED</span>
              <span className={styles.selectedAddress}>{address}</span>
            </div>
          </div>
        )}
      </div>

      {/* Section 02: Delivery Details */}
      {showDetailFields && isLocationSelected && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>02</span>
            <span className={styles.sectionTitle}>DELIVERY DETAILS</span>
          </div>
          
          <div className={styles.detailsGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldIcon}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </span>
                BUILDING / HOUSE NO.
              </label>
              <input
                type="text"
                className={styles.input}
                value={buildingNumber}
                onChange={(e) => setBuildingNumber(e.target.value)}
                placeholder="No. 123, Block A"
              />
            </div>
            
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldIcon}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="0"/>
                    <path d="M3 9h18M3 15h18M9 3v18"/>
                  </svg>
                </span>
                FLOOR / UNIT
              </label>
              <input
                type="text"
                className={styles.input}
                value={floor}
                onChange={(e) => setFloor(e.target.value)}
                placeholder="2nd Floor, Unit 5"
              />
            </div>
          </div>
          
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldIcon}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
              </span>
              DELIVERY INSTRUCTIONS
            </label>
            <textarea
              className={styles.textarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ring doorbell twice, leave at gate if not home, call before arrival..."
              rows={3}
            />
            <span className={styles.fieldHint}>
              Help our driver find you faster
            </span>
          </div>
        </div>
      )}

      {/* Coordinates Display (collapsed) */}
      {selectedCoords && (
        <div className={styles.coordsBar}>
          <span className={styles.coordsLabel}>COORDS</span>
          <span className={styles.coordsValue}>
            {selectedCoords.lat.toFixed(6)}, {selectedCoords.lng.toFixed(6)}
          </span>
        </div>
      )}

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <h3 className={styles.modalTitle}>Enable Location Access</h3>
            </div>
            
            <div className={styles.modalBody}>
              <p className={styles.modalText}>
                To use GPS, please enable location access for this website in your browser or device settings.
              </p>
              
              <div className={styles.modalInstructions}>
                <div className={styles.instructionGroup}>
                  <span className={styles.instructionNumber}>01</span>
                  <div className={styles.instructionContent}>
                    <span className={styles.instructionLabel}>Safari (iOS/Mac)</span>
                    <p className={styles.instructionText}>
                      Settings → Safari → Location → Allow
                    </p>
                  </div>
                </div>
                
                <div className={styles.instructionGroup}>
                  <span className={styles.instructionNumber}>02</span>
                  <div className={styles.instructionContent}>
                    <span className={styles.instructionLabel}>Chrome</span>
                    <p className={styles.instructionText}>
                      Lock icon → Site settings → Location → Allow
                    </p>
                  </div>
                </div>
                
                <div className={styles.instructionGroup}>
                  <span className={styles.instructionNumber}>03</span>
                  <div className={styles.instructionContent}>
                    <span className={styles.instructionLabel}>iOS Device</span>
                    <p className={styles.instructionText}>
                      Settings → Privacy → Location Services → Safari
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.modalActions}>
              <button 
                type="button" 
                className={styles.modalButtonSecondary}
                onClick={handleCloseModal}
              >
                Use Search Instead
              </button>
              <button 
                type="button" 
                className={styles.modalButtonPrimary}
                onClick={() => {
                  handleCloseModal()
                  handleUseMyLocation()
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Permission Modal - explains before browser prompt */}
      {showPrePermissionModal && (
        <div className={styles.modalOverlay} onClick={() => setShowPrePermissionModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v4m0 12v4M2 12h4m12 0h4"/>
                </svg>
              </div>
              <h3 className={styles.modalTitle}>Use Your Location?</h3>
            </div>
            
            <div className={styles.modalBody}>
              <p className={styles.modalText}>
                We'll use GPS to find your exact location and auto-fill your delivery address. This makes ordering faster and more accurate.
              </p>
              
              <div className={styles.permissionNote}>
                <div className={styles.permissionNoteIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4m0-4h.01"/>
                  </svg>
                </div>
                <p className={styles.permissionNoteText}>
                  Your browser will ask for permission. Tap <strong>"Allow"</strong> when prompted to enable location access.
                </p>
              </div>
            </div>
            
            <div className={styles.modalActions}>
              <button 
                type="button" 
                className={styles.modalButtonSecondary}
                onClick={() => {
                  setShowPrePermissionModal(false)
                  setHasAskedPermission(true)
                }}
              >
                No Thanks
              </button>
              <button 
                type="button" 
                className={styles.modalButtonPrimary}
                onClick={() => {
                  setShowPrePermissionModal(false)
                  triggerLocationRequest()
                }}
              >
                Enable GPS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
